import { forwardRef, useState } from 'react';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { BatchSelect } from '@modules/Batches/components/BatchSelect';
import { useTranslation } from 'react-i18next';
import { useCompleteLine } from '../../hooks';
import { ExtraWorkOrderLine } from '../../models';

interface CompleteLineModalProps {
  orderId: number;
  line: ExtraWorkOrderLine;
  onSuccess?: () => void;
}

/**
 * Modal para completar una línea de trabajo
 */
export const CompleteLineModal = forwardRef<ModalCenterRef, CompleteLineModalProps>(
  ({ orderId, line, onSuccess }, ref) => {
    const { t } = useTranslation('extraWorkOrdersModule');
    const completeLineMutation = useCompleteLine();

    // Estado del formulario
    const [doneLocationCode, setDoneLocationCode] = useState<string>('');
    const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);

    const resetForm = () => {
      setDoneLocationCode('');
      setSelectedBatchId(null);
    };

    // Handlers
    const handleCancel = () => {
      (ref as React.RefObject<ModalCenterRef>)?.current?.closeModal();
      resetForm();
    };

    const handleSubmit = async () => {
      // Validaciones
      if (!doneLocationCode || !doneLocationCode.trim()) {
        // El toast se mostrará desde el hook
        return;
      }

      // Validar que la merma no exceda lo esperado
      if (line.qty_waste > line.qty_expected) {
        // El toast se mostrará desde el hook
        return;
      }

      try {
        await completeLineMutation.mutateAsync({
          orderId,
          lineId: line.id,
          data: {
            quantity_completed: line.qty_done || line.qty_expected,
            location_code: doneLocationCode.trim(),
            ...(selectedBatchId && { batch_id: selectedBatchId }),
          },
        });

        handleCancel();
        onSuccess?.();
      } catch {
        // El error ya se maneja en el hook
      }
    };

    return (
      <ModalCenter ref={ref} title={t('modals.completeLine.title', 'Completar Línea')} width="lg">
        <div className="space-y-6">
          {/* Resumen */}
          <div className="p-4 bg-card-bg border border-card-border rounded-lg">
            <h4 className="text-sm font-medium text-text mb-4">{t('modals.completeLine.summary', 'Resumen')}</h4>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-subtext mb-1">
                  {t('modals.completeLine.qty_expected', 'Cantidad esperada')}
                </label>
                <p className="text-sm font-medium text-text">{line.qty_expected}</p>
              </div>
              <div>
                <label className="block text-xs text-subtext mb-1">
                  {t('modals.completeLine.qty_waste', 'Cantidad merma')}
                </label>
                <p className="text-sm font-medium text-text">{line.qty_waste}</p>
              </div>
              <div>
                <label className="block text-xs text-subtext mb-1">
                  {t('modals.completeLine.qty_done', 'Cantidad realizada')}
                </label>
                <p className="text-sm font-medium text-text">{line.qty_done}</p>
              </div>
            </div>
          </div>

          {/* Ubicación destino */}
          <div>
            <label className="block text-sm font-medium text-text mb-2">
              {t('modals.completeLine.done_location', 'Ubicación destino')} *
            </label>
            <p className="text-sm text-subtext mb-2">
              {t('modals.completeLine.done_location_help', 'Seleccione o escanee la ubicación móvil destino')}
            </p>
            <Input
              type="text"
              value={doneLocationCode}
              onChange={(e) => setDoneLocationCode(e.target.value)}
              placeholder={t('modals.completeLine.done_location', 'Ubicación destino')}
              data-testid="done-location-input"
            />
          </div>

          <BatchSelect productId={line.product_id} value={selectedBatchId} onChange={setSelectedBatchId} />

          {/* Acciones */}
          <div className="flex justify-end gap-2 pt-4 border-t border-lines">
            <Button variant="outline" onClick={handleCancel} data-testid="cancel-button">
              {t('modals.completeLine.cancel', 'Cancelar')}
            </Button>
            <Button onClick={handleSubmit} disabled={completeLineMutation.isPending} data-testid="submit-button">
              {completeLineMutation.isPending
                ? t('modals.completeLine.submitting', 'Completando...')
                : t('modals.completeLine.submit', 'Completar línea')}
            </Button>
          </div>
        </div>
      </ModalCenter>
    );
  },
);

CompleteLineModal.displayName = 'CompleteLineModal';
